import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import {
  CreditCard, DollarSign, Users, Calendar, Download, AlertCircle,
  CheckCircle, Receipt, Building2, Sparkles, Clock, Crown
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const BillingDashboard = ({ company, token, canManage = true }) => {
  const { toast } = useToast();
  const [activeUsers, setActiveUsers] = useState(0);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [cardStatus, setCardStatus] = useState(null);

  useEffect(() => {
    if (token) {
      fetchActiveUsers();
      fetchCardStatus();
      if (canManage) syncSubscriptionIfNeeded();
    }
  }, [token]);

  // Self-heal: if this company isn't marked active but Stripe says they have
  // an active subscription (paid but webhook missed), flip them to active and
  // refresh so the trial/grace banner disappears.
  const syncSubscriptionIfNeeded = async () => {
    if (company?.subscription_status === 'active') return;
    try {
      const r = await axios.post(`${API}/stripe/sync-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.data?.activated) {
        toast({ title: 'Payment confirmed 🎉', description: 'Your subscription is now active. Refreshing…' });
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (e) {
      // 403 for non-owners is expected; stay quiet
    }
  };

  const fetchCardStatus = async () => {
    try {
      const response = await axios.get(`${API}/stripe/card-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCardStatus(response.data);
    } catch (e) {
      console.error('Failed to fetch card status:', e);
    }
  };

  const fetchActiveUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { scope: 'all' }
      });
      setActiveUsers(response.data.length);
    } catch (e) {
      console.error('Failed to fetch users:', e);
    }
  };

  // ── Derived values ──
  const currency = (company?.currency || 'usd').toLowerCase();
  const currencyLabel = { usd: 'USD', cad: 'CAD', eur: 'EUR' }[currency] || currency.toUpperCase();
  const symbols = { usd: '$', cad: 'C$', eur: '€', gbp: '£' };
  const symbol = symbols[currency] || '$';
  const perSeatRate = Number(company?.monthly_cost ?? 5.99);
  // Seat count source priority:
  //   1. Live count from the /api/users fetch when it's known (most accurate)
  //   2. company.active_users_count from /users/me (now also live-computed
  //      server-side, but the local fetch above usually fires first)
  //   3. 0 fallback
  // The previous logic used `company?.active_users_count ?? activeUsers`,
  // which pinned the tile to whatever stale value was on the company doc
  // (typically `1` from registration) regardless of how many seats were
  // actually added later.
  const seatCount = activeUsers > 0
    ? activeUsers
    : (company?.active_users_count ?? 0);
  const monthlyCost = seatCount * perSeatRate;
  const setupFee = 9.99;

  // Trial-end date — when free service ends. After this date the user is
  // billed monthly. Source of truth is `trial_end_date`; falls back to
  // created_at / setup_fee_paid_at + 30 days.
  const computeTrialEndDate = () => {
    if (company?.trial_end_date) return new Date(company.trial_end_date);
    if (company?.setup_fee_paid_at) {
      const d = new Date(company.setup_fee_paid_at);
      d.setDate(d.getDate() + 30);
      return d;
    }
    if (company?.created_at) {
      const d = new Date(company.created_at);
      d.setDate(d.getDate() + 30);
      return d;
    }
    return null;
  };

  // Hines Time bills on the **1st of every month**. The next billing date is:
  //   • If still in trial → the 1st of the month *after* the trial ends.
  //   • If active → the 1st of next month from today.
  // Frontend-only calculation; aligning Stripe to actually charge on this day
  // is a separate backlog task (requires real Stripe Subscription objects
  // with `billing_cycle_anchor` set to the 1st of the next month).
  const nextFirstOfMonth = (after) => {
    const base = after ? new Date(after) : new Date();
    return new Date(base.getFullYear(), base.getMonth() + 1, 1);
  };

  const trialEnd = computeTrialEndDate();
  const trialActive = company?.subscription_status === 'trial' && trialEnd && trialEnd > new Date();
  const nextBilling = trialActive ? nextFirstOfMonth(trialEnd) : nextFirstOfMonth(new Date());
  const now = new Date();
  const daysToNextBilling = nextBilling
    ? Math.max(0, Math.ceil((nextBilling - now) / 86400000))
    : null;

  const fmtDate = (d) => d ? d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
  const fmtMoney = (n) => `${symbol}${Number(n).toFixed(2)}`;

  const statusInfo = {
    trial: { label: 'Free Trial', tone: 'bg-blue-100 text-blue-900 dark:bg-blue-950/60 dark:text-blue-200', icon: Sparkles },
    active: { label: 'Active', tone: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200', icon: CheckCircle },
    past_due: { label: 'Past Due', tone: 'bg-red-100 text-red-900 dark:bg-red-950/60 dark:text-red-200', icon: AlertCircle },
    cancelled: { label: 'Cancelled', tone: 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-200', icon: AlertCircle }
  }[company?.subscription_status] || { label: 'Free Trial', tone: 'bg-blue-100 text-blue-900 dark:bg-blue-950/60 dark:text-blue-200', icon: Sparkles };

  const StatusIcon = statusInfo.icon;

  // ── Actions ──
  const handlePaySetupFee = async () => {
    setLoadingPortal(true);
    try {
      const response = await axios.post(`${API}/stripe/create-checkout-session`, null, {
        params: {
          payment_type: 'setup_fee',
          origin_url: window.location.origin,
          currency
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.url) window.location.href = response.data.url;
    } catch (error) {
      toast({
        title: 'Stripe error',
        description: error.response?.data?.detail || 'Failed to open payment session',
        variant: 'destructive'
      });
    } finally {
      setLoadingPortal(false);
    }
  };

  // Update payment method / view invoices — opens the Stripe Billing Portal
  // when a subscription exists, otherwise a subscription checkout that saves
  // the card (the old flow only took a one-time payment and saved nothing).
  const handleManageBilling = async (plan) => {
    setLoadingPortal(true);
    try {
      const response = await axios.post(
        `${API}/stripe/payment-portal`,
        { origin_url: window.location.origin, ...(plan === 'annual' ? { plan: 'annual' } : {}) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.mode === 'already_active') {
        toast({ title: 'Payment confirmed 🎉', description: 'Your subscription is already active. Refreshing…' });
        setTimeout(() => window.location.reload(), 1500);
      } else if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast({ title: 'Stripe error', description: 'No payment link returned. Please try again.', variant: 'destructive' });
      }
    } catch (error) {
      toast({
        title: 'Stripe error',
        description: error.response?.data?.detail || 'Failed to open the payment portal',
        variant: 'destructive'
      });
    } finally {
      setLoadingPortal(false);
    }
  };

  const handleViewInvoices = () => handleManageBilling();

  return (
    <Card className="shadow-lg border" data-testid="billing-dashboard">
      <CardHeader className="bg-gradient-to-r from-emerald-500 to-blue-600 text-white border-b">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-white text-2xl">
              <Crown className="w-6 h-6" />
              Billing & Subscription
            </CardTitle>
            <p className="text-sm text-white/80 mt-1">
              Super Admin only · {company?.company_name || 'Your company'}
            </p>
          </div>
          <Badge className={`${statusInfo.tone} font-semibold px-3 py-1.5 text-sm`} data-testid="billing-status-badge">
            <StatusIcon className="w-4 h-4 mr-1.5 inline" />
            {statusInfo.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        {/* ── At-a-glance stat tiles ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="billing-stat-tiles">
          {/* Seats */}
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-blue-50 dark:bg-blue-950/40 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-blue-700 dark:text-blue-300">User Seats</div>
                <div className="text-3xl font-bold text-blue-900 dark:text-blue-100 mt-1" data-testid="billing-seats-count">{seatCount}</div>
                <div className="text-xs text-blue-700/80 dark:text-blue-300/80 mt-0.5">{fmtMoney(perSeatRate)} per seat / month</div>
              </div>
              <Users className="w-10 h-10 text-blue-500/70" />
            </div>
          </div>

          {/* Monthly cost */}
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-emerald-50 dark:bg-emerald-950/40 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-emerald-700 dark:text-emerald-300">Monthly Total</div>
                <div className="text-3xl font-bold text-emerald-900 dark:text-emerald-100 mt-1" data-testid="billing-monthly-total">
                  {fmtMoney(monthlyCost)}
                </div>
                <div className="text-xs text-emerald-700/80 dark:text-emerald-300/80 mt-0.5">{currencyLabel} · auto-adjusts with seats</div>
              </div>
              <DollarSign className="w-10 h-10 text-emerald-500/70" />
            </div>
          </div>

          {/* Next billing date */}
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-violet-50 dark:bg-violet-950/40 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-violet-700 dark:text-violet-300">Next Payment</div>
                <div className="text-2xl font-bold text-violet-900 dark:text-violet-100 mt-1" data-testid="billing-next-payment-date">
                  {fmtDate(nextBilling)}
                </div>
                <div className="text-xs text-violet-700/80 dark:text-violet-300/80 mt-0.5">
                  {daysToNextBilling != null ? `${daysToNextBilling} days from today` : 'Not scheduled yet'}
                </div>
              </div>
              <Calendar className="w-10 h-10 text-violet-500/70" />
            </div>
          </div>
        </div>

        {/* ── Trial banner (if applicable) ── */}
        {trialActive && (
          <div className="rounded-lg border-2 border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/40 p-4" data-testid="billing-trial-banner">
            <div className="flex items-start gap-3">
              <Sparkles className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-blue-900 dark:text-blue-100">
                  Free trial · ends {fmtDate(trialEnd)}
                </div>
                <div className="text-sm text-blue-800/80 dark:text-blue-200/80 mt-1 space-y-1">
                  <p>
                    You currently have <strong>{seatCount} seat{seatCount !== 1 ? 's' : ''}</strong>.
                    Once your trial ends, you'll be billed <strong>{fmtMoney(monthlyCost)}/month</strong> ({seatCount} × {fmtMoney(perSeatRate)})
                    on the <strong>1st of every month</strong>.
                  </p>
                  <p>Your first bill is scheduled for <strong>{fmtDate(nextBilling)}</strong>. Add or remove seats anytime — billing auto-adjusts to your live headcount.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Setup fee banner ── */}
        {!company?.setup_fee_paid && (
          <div className="rounded-lg border-2 border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-950/40 p-4" data-testid="billing-setup-banner">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-orange-900 dark:text-orange-100">
                    One-time setup fee outstanding · {fmtMoney(setupFee)}
                  </div>
                  <div className="text-sm text-orange-800/80 dark:text-orange-200/80 mt-0.5">
                    Pay your one-time setup fee to activate your 30-day free trial.
                  </div>
                </div>
              </div>
              {canManage && <Button onClick={handlePaySetupFee} disabled={loadingPortal} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="billing-pay-setup-btn">
                Pay Setup Fee
              </Button>}
            </div>
          </div>
        )}

        {/* ── General billing information ── */}
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900/60" data-testid="billing-general-info">
          <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
            <Receipt className="w-4 h-4 text-slate-600 dark:text-slate-400" />
            Billing Information
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div className="flex justify-between sm:block">
              <span className="text-slate-500 dark:text-slate-400">Company</span>
              <span className="font-medium text-slate-900 dark:text-slate-100 sm:block">{company?.company_name || '—'}</span>
            </div>
            <div className="flex justify-between sm:block">
              <span className="text-slate-500 dark:text-slate-400">Currency</span>
              <span className="font-medium text-slate-900 dark:text-slate-100 sm:block">{currencyLabel}</span>
            </div>
            <div className="flex justify-between sm:block">
              <span className="text-slate-500 dark:text-slate-400">Plan</span>
              <span className="font-medium text-slate-900 dark:text-slate-100 sm:block">Pay-per-seat · {fmtMoney(perSeatRate)}/seat/month</span>
            </div>
            <div className="flex justify-between sm:block">
              <span className="text-slate-500 dark:text-slate-400">Setup Fee</span>
              <span className="font-medium sm:block">
                {company?.setup_fee_paid ? (
                  <span className="text-emerald-700 dark:text-emerald-400">
                    Paid · {company.setup_fee_paid_at ? new Date(company.setup_fee_paid_at).toLocaleDateString() : ''}
                  </span>
                ) : (
                  <span className="text-orange-700 dark:text-orange-400">Pending · {fmtMoney(setupFee)}</span>
                )}
              </span>
            </div>
            <div className="flex justify-between sm:block">
              <span className="text-slate-500 dark:text-slate-400">Payment Method</span>
              <span className="font-medium sm:block" data-testid="billing-card-status">
                {cardStatus == null ? (
                  <span className="text-slate-400">Checking…</span>
                ) : cardStatus.card_on_file ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400" data-testid="billing-card-on-file">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Card saved · {(cardStatus.brand || 'card').toUpperCase()} ••••{cardStatus.last4}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-orange-700 dark:text-orange-400" data-testid="billing-no-card">
                    <AlertCircle className="w-3.5 h-3.5" />
                    No card on file
                  </span>
                )}
              </span>
            </div>
            <div className="flex justify-between sm:block">
              <span className="text-slate-500 dark:text-slate-400">Stripe Customer</span>
              <span className="font-mono text-xs text-slate-700 dark:text-slate-300 sm:block truncate" data-testid="billing-stripe-customer-id">
                {company?.stripe_customer_id || 'Not yet linked'}
              </span>
            </div>
            <div className="flex justify-between sm:block">
              <span className="text-slate-500 dark:text-slate-400">Account Created</span>
              <span className="font-medium text-slate-900 dark:text-slate-100 sm:block">
                {company?.created_at ? new Date(company.created_at).toLocaleDateString() : '—'}
              </span>
            </div>
          </div>
        </div>

        {/* ── Cost breakdown table ── */}
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="billing-cost-breakdown">
          <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/60 text-xs uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-400 flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5" />
            Monthly cost breakdown
          </div>
          <div className="divide-y divide-slate-200 dark:divide-slate-700 text-sm">
            <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-900/60">
              <div className="text-slate-700 dark:text-slate-300">
                <div>{seatCount} seat{seatCount !== 1 ? 's' : ''} × {fmtMoney(perSeatRate)}</div>
                <div className="text-xs text-slate-500 dark:text-slate-500 mt-0.5">Per-user subscription · auto-adjusts</div>
              </div>
              <div className="font-semibold text-slate-900 dark:text-slate-100">{fmtMoney(monthlyCost)}</div>
            </div>
            <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 dark:bg-emerald-950/40">
              <div className="font-semibold text-emerald-900 dark:text-emerald-100">Monthly billing total</div>
              <div className="text-xl font-bold text-emerald-700 dark:text-emerald-300">{fmtMoney(monthlyCost)} <span className="text-xs font-normal text-emerald-700/70 dark:text-emerald-300/70">/ mo</span></div>
            </div>
          </div>
        </div>

        {/* ── Action buttons (account owner only — admins see billing read-only) ── */}
        {!canManage && (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-600 dark:text-slate-300 flex items-start gap-2" data-testid="billing-readonly-note">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Payment methods and plan changes are managed by the account owner (the person who signed up the company). You can view billing status here and will be notified about billing events.</span>
          </div>
        )}
        {canManage && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            onClick={() => handleManageBilling()}
            disabled={loadingPortal}
            variant="outline"
            className="justify-start"
            data-testid="billing-update-payment-btn"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            {loadingPortal ? 'Opening Stripe…' : 'Update Payment Method'}
          </Button>
          {company?.subscription_status !== 'active' && (
            <Button
              onClick={() => handleManageBilling('annual')}
              disabled={loadingPortal}
              variant="outline"
              className="justify-start border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400"
              data-testid="billing-annual-plan-btn"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Subscribe Annually — $59.90/seat/yr (2 months free)
            </Button>
          )}
          <Button
            onClick={handleViewInvoices}
            disabled={loadingPortal}
            variant="outline"
            className="justify-start"
            data-testid="billing-view-invoices-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            View Invoice History
          </Button>
        </div>}

        {/* ── Footer info note ── */}
        <div className="text-xs text-slate-500 dark:text-slate-400 flex items-start gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
          <Clock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            HinesTime uses metered, per-seat billing — <strong>{fmtMoney(perSeatRate)} per active user per month</strong>, billed on the <strong>1st of every month</strong>.
            Add or remove users in <strong>Settings → Manage Users</strong> any day; we charge based on your headcount on the billing date.
            All payments are processed securely by Stripe.
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default BillingDashboard;
